import { AnalyticsHandler } from './analytics/analyticsHandler';
import { bannerResizeHandler } from './globalDomHandlers/bannerResizeHandler';
import { gaNonInteractionUserEventHandler } from './globalDomHandlers/gaNonInteractionUserEventHandler';
import gaPromoViewHandler from './globalDomHandlers/gaPromoViewHandler';
import videoMuteHandler from './globalDomHandlers/videoClickHandler';
import { cleanTokenSessionStorageIfNeeded } from './microApps/common/api/auth/getAuthToken';
import { ga4MiscTracking } from './microApps/common/tracking/ga4/misc';
import { syncConsentManagerVendors } from './utility/gdpr';

const globalInvokes = () => {
    AnalyticsHandler.initialize();

    syncConsentManagerVendors();
    cleanTokenSessionStorageIfNeeded();

    handleGlobalClick();
    handleGlobalEvents();

    // it is here to remove old ab test local storage keys. We can remove after some time. The ab tests will now be kept inside single key coop_abtest.
    cleanLocalStorage(['abtest_']);
};

const cleanLocalStorage = (keysToRemove: string[]) => {
    if (!keysToRemove.length) return;

    const storageKeys = Object.keys(window.localStorage);

    const storageKeysToRemove = storageKeys.filter((sk) =>
        keysToRemove.some((ktr) => sk.startsWith(ktr)),
    );

    storageKeysToRemove.forEach((sktr) => window.localStorage.removeItem(sktr));
    if (storageKeysToRemove.length) {
        // eslint-disable-next-line no-console
        console.warn(`Removed storage Keys: ${storageKeysToRemove.join(',')}`);
    }
};

const handleGlobalClick = () => {
    // Never call stopPropagation or stopImmediatePropagation inside this function as it is running in capture phase
    // It would break most of other click handlers on the page, i.e. SPA navigation link clicks
    const onClickCapture = (event: Event) => {
        const element = event.target as HTMLElement;

        const closestAnchor = element.closest('a');

        const partnerName =
            closestAnchor?.dataset.name ??
            (document.querySelector('.js-gaPartnerName') as HTMLElement | null)?.dataset.name ??
            null;

        const additionalData = partnerName ? { partner: partnerName || null } : {};

        if (element instanceof HTMLAnchorElement && element.href) {
            ga4MiscTracking.outboundClick(element.href, element.innerText, additionalData);
            return;
        }

        if (closestAnchor) {
            ga4MiscTracking.outboundClick(
                closestAnchor.href,
                closestAnchor.innerText,
                additionalData,
            );
        }

        if (element && element.classList.contains('js-videoMute')) {
            videoMuteHandler(element);
        }
    };

    document.addEventListener('click', onClickCapture, {
        // Event is captured during the capture phase to ensure it runs before any stopPropagation calls in other click handlers (i.e. in React code)
        // This handler needs to be captured here since other click handlers on the page may call stopPropagation/preventDefault which would prevent this handler from running
        // I.e. React Aria's useButton calls stopPropagation internally, breaking our global click handler if we use bubbling phase (thats the default) instead of capture phase here
        capture: true,
    });
};

const handleGlobalEvents = () => {
    bannerResizeHandler();

    setTimeout(gaNonInteractionUserEventHandler, 1000);
    document.querySelectorAll<HTMLElement>('.js-gaPromo').forEach((element) => {
        gaPromoViewHandler(element);
    });
};

export default globalInvokes;
